import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Loader2, FileText, CheckCircle, XCircle,
  AlertTriangle, Download, Play, Trash2, RefreshCw,
} from 'lucide-react';
import api from '../../lib/api';
import { useAppDialog } from '@/hooks/useAppDialog';

const DATASET_TYPES = [
  { value: 'participants', label: 'Participants' },
  { value: 'staff', label: 'Staff' },
  { value: 'service_catalog', label: 'Service Catalog' },
];

const IMPORT_STATUS_MAP = {
  uploaded: { label: 'Uploaded', color: 'bg-blue-100 text-blue-700', icon: FileText },
  validated: { label: 'Validated', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  partially_valid: { label: 'Partial', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  committed: { label: 'Committed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: Trash2 },
};

export default function OnboardingImportPage() {
  const { confirm } = useAppDialog();
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState('');
  const [committing, setCommitting] = useState('');
  const [datasetType, setDatasetType] = useState('participants');

  const fetchImports = useCallback(async () => {
    try {
      const res = await api.get(`/client-onboarding/${id}/imports`);
      setImports(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load imports');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchImports(); }, [fetchImports]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { toast.error('Only CSV files are supported'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('datasetType', datasetType);
      const res = await api.post(`/client-onboarding/${id}/imports/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded: ${res.data.rowCount} rows`);
      fetchImports();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleValidate = async (importId) => {
    setValidating(importId);
    try {
      const res = await api.post(`/client-onboarding/${id}/imports/${importId}/validate`);
      const s = res.data.summary;
      toast.success(`Validated: ${s.validRowCount} valid, ${s.invalidRowCount} invalid`);
      fetchImports();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Validation failed');
    }
    setValidating('');
  };

  const handleCommit = async (importId) => {
    const ok = await confirm({
      title: 'Commit valid rows?',
      description: 'This will create records in the system from all rows that passed validation. Invalid rows will be skipped.',
      confirmLabel: 'Commit rows',
      variant: 'warning',
      testId: 'import-commit-confirm',
    });
    if (!ok) return;
    setCommitting(importId);
    try {
      const res = await api.post(`/client-onboarding/${id}/imports/${importId}/commit`);
      const r = res.data.commitResult;
      toast.success(`Committed: ${r.createdCount} created, ${r.skippedCount} skipped`);
      fetchImports();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Commit failed');
    }
    setCommitting('');
  };

  const handleCancel = async (importId) => {
    const ok = await confirm({
      title: 'Cancel import?',
      description: 'Cancelling this import discards any uploaded rows. You can upload a fresh CSV to start again.',
      confirmLabel: 'Cancel import',
      cancelLabel: 'Keep',
      testId: 'import-cancel-confirm',
    });
    if (!ok) return;
    try {
      await api.post(`/client-onboarding/${id}/imports/${importId}/cancel`);
      toast.success('Import cancelled');
      fetchImports();
    } catch (e) {
      toast.error('Failed to cancel');
    }
  };

  const handleDownloadTemplate = async (type) => {
    try {
      const res = await api.get(`/client-onboarding/templates/${type}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${type}_template.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-import-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/onboarding/${id}`)} data-testid="back-to-detail">
          <ArrowLeft className="h-4 w-4 mr-1" />Back to Detail
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Import Workspace</h1>
          <p className="text-sm text-muted-foreground">Upload, validate, and commit CSV data imports</p>
        </div>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Upload New Import</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-1">Dataset Type</p>
              <Select value={datasetType} onValueChange={setDatasetType}>
                <SelectTrigger data-testid="dataset-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DATASET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate(datasetType)} data-testid="download-template-btn">
                <Download className="h-4 w-4 mr-1" />Template
              </Button>
              <div className="relative">
                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleUpload} data-testid="file-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn">
                  {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                  Upload CSV
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Imports List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Import History</CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchImports} data-testid="refresh-imports">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : imports.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">No imports yet. Upload a CSV to get started.</p>
          ) : (
            <div className="space-y-4">
              {imports.map(imp => {
                const sCfg = IMPORT_STATUS_MAP[imp.status] || { label: imp.status, color: 'bg-gray-100 text-gray-600' };
                const Icon = sCfg.icon || FileText;
                return (
                  <div key={imp.id} className="border rounded-lg p-4" data-testid={`import-${imp.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-sm font-medium">{imp.fileName}</p>
                          <p className="text-xs text-muted-foreground capitalize">{imp.datasetType} | {imp.summary?.rowCount || 0} rows</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={`${sCfg.color} text-xs`}>{sCfg.label}</Badge>
                    </div>

                    {/* Summary */}
                    {imp.summary && (
                      <div className="flex gap-4 mt-3 text-xs">
                        <span className="text-emerald-600">Valid: {imp.summary.validRowCount}</span>
                        <span className="text-red-600">Invalid: {imp.summary.invalidRowCount}</span>
                        <span className="text-amber-600">Warnings: {imp.summary.warningCount}</span>
                      </div>
                    )}

                    {/* Commit Result */}
                    {imp.commitResult && (
                      <div className="flex gap-4 mt-2 text-xs bg-muted/50 p-2 rounded">
                        <span>Created: {imp.commitResult.createdCount}</span>
                        <span>Skipped: {imp.commitResult.skippedCount}</span>
                        <span>Failed: {imp.commitResult.failedCount}</span>
                      </div>
                    )}

                    {/* Errors */}
                    {imp.errors?.length > 0 && (
                      <div className="mt-2 max-h-24 overflow-y-auto">
                        {imp.errors.slice(0, 5).map((err, i) => (
                          <p key={i} className="text-xs text-red-600">Row {err.rowNumber}: {err.message}</p>
                        ))}
                        {imp.errors.length > 5 && <p className="text-xs text-muted-foreground">...and {imp.errors.length - 5} more</p>}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 mt-3">
                      {imp.status === 'uploaded' && (
                        <Button size="sm" variant="outline" onClick={() => handleValidate(imp.id)}
                                disabled={validating === imp.id} data-testid={`validate-${imp.id}`}>
                          {validating === imp.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                          Validate
                        </Button>
                      )}
                      {['validated', 'partially_valid'].includes(imp.status) && (
                        <Button size="sm" onClick={() => handleCommit(imp.id)}
                                disabled={committing === imp.id} data-testid={`commit-${imp.id}`}>
                          {committing === imp.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 mr-1" />}
                          Commit Valid Rows
                        </Button>
                      )}
                      {!['committed', 'cancelled'].includes(imp.status) && (
                        <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => handleCancel(imp.id)} data-testid={`cancel-${imp.id}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" />Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
